/**
 * Stormcaller: ranged DPS (PLAN §4), rotation rework (docs/design/classes/stormcaller.md).
 * Elemental shape with Frost Mage rhythm:
 *  - Flame Shock ticks roll **Lava Surge** (6%, +3% per miss, 4 s ICD): +1 Lava Burst charge and an instant Lava Burst.
 *  - Lava Burst (2 charges) always crits on Flame Shock and grants **Master of the Elements**
 *    (+20% to the next Earth Shock, Tempest or Chain Lightning).
 *  - Lightning Bolt, Tempest and Chain Lightning (per enemy hit) build **Static Charge** (max 5);
 *    Earth Shock (5) and Earthquake (4) cash it in at +8% per stack.
 *  - Every 3rd step (Gathering Storm counter: Earth Shock, Earthquake or a full Arc Torrent) turns
 *    Lightning Bolt into **Tempest** (transform).
 *  - **Arc Torrent** (6, 30 s channel) ends by granting Lava Surge and setting Static Charge to 5:
 *    the short burst window. **Stormkeeper** (Q, 60 s) is the major one.
 *
 * Budget (single target, modelled 60 s): ≈ 255 coef/min ≈ 4.3k DPS at 1000 power (old kit ≈ 4.3k).
 */
import type { AbilityDef, AuraDef, ClassDef, EffectSpec } from '@keystone/sim';
import { ABILITY, AURA, CLASS, PASSIVE } from '../ids';
import { PLAYER_HP, baseStats } from '../balance';
import { OFF_GCD, SPELL_RANGE, aroundTarget, dodge, interrupt, selfBuff } from './common';
import { STORMCALLER_UNLOCKS } from './unlocks';

const A = ABILITY.stormcaller;

/** Stormkeeper empowers these (Lava Burst is no longer on the list: Lava Surge owns instant Lava Bursts). */
const STORMKEEPER_EMPOWERS = [A.lightningBolt, A.tempest, A.chainLightning];
/** Master of the Elements empowers (and is consumed by) these. */
const MOTE_EMPOWERS = [A.earthShock, A.tempest, A.chainLightning];
/** Static Charge spenders. */
const SPENDERS = [A.earthShock, A.earthquake];

/** Other enemies near the primary target (never the primary itself). */
const othersNearTarget = (radius: number, maxTargets: number) => ({ ...aroundTarget(radius, maxTargets), excludePrimary: true });

// ---------------------------------------------------------------------------
// Auras (one const per id: the registry keys auras by id, every copy must be identical)
// ---------------------------------------------------------------------------

/** Proc: +1 Lava Burst charge (granted with it) and the next Lava Burst is instant. */
export const LAVA_SURGE: AuraDef = {
  id: AURA.lavaSurge,
  name: 'Lava Surge',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [A.lavaBurst], ignoreStacks: true }],
  // castResolved + same-cast guard: a Lava Surge that procs during a hard-cast Lava Burst is kept.
  procs: [{ on: 'castResolved', abilityIds: [A.lavaBurst], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Buff, not a proc: the next Earth Shock, Tempest or Chain Lightning deals 20% more. No glow (it would light after every Lava Burst). */
export const MASTER_OF_ELEMENTS: AuraDef = {
  id: AURA.masterOfElements,
  name: 'Master of the Elements',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.2, abilityIds: MOTE_EMPOWERS }],
  procs: [{ on: 'castResolved', abilityIds: MOTE_EMPOWERS, consumeStack: true, effects: [] }],
  ui: { glow: false },
};

/** Stack (max 5): +8% per stack to Earth Shock and Earthquake, which remove it whole. Glows them at 5. */
export const STATIC_CHARGE: AuraDef = {
  id: AURA.staticCharge,
  name: 'Static Charge',
  kind: 'buff',
  durationMs: 20000,
  stacking: 'stack',
  maxStacks: 5,
  mods: [{ stat: 'damageDonePct', value: 0.08, abilityIds: SPENDERS }],
  ui: { glow: { abilityIds: SPENDERS, atStacks: 5 }, stackCounterOn: SPENDERS },
};

/** Spender counter: the 3rd step (Earth Shock, Earthquake or a full Arc Torrent) clears it and grants Tempest. */
export const GATHERING_STORM: AuraDef = {
  id: AURA.gatheringStorm,
  name: 'Gathering Storm',
  kind: 'buff',
  durationMs: 30000,
  stacking: 'stack',
  maxStacks: 2,
};

/** Transform: Lightning Bolt (key 1) casts Tempest while this is up. Tempest removes it. */
export const TEMPEST_READY: AuraDef = {
  id: AURA.tempest,
  name: 'Tempest',
  kind: 'buff',
  durationMs: 20000,
  stacking: 'refresh',
  overrides: [{ abilityId: A.lightningBolt, with: A.tempest }],
  ui: { proc: true },
};

/** Major cooldown buff: next 3 Lightning Bolts, Tempests or Chain Lightnings are instant and deal +50%. */
export const STORMKEEPER: AuraDef = {
  id: AURA.stormkeeper,
  name: 'Stormkeeper',
  kind: 'buff',
  durationMs: 15000,
  stacking: 'stack',
  maxStacks: 3,
  mods: [
    { stat: 'castTimePct', value: -1, abilityIds: STORMKEEPER_EMPOWERS, ignoreStacks: true },
    // Flat +50% on every empowered cast (F8; the old per-stack value gave the 2nd cast +0%).
    { stat: 'damageDonePct', value: 0.5, abilityIds: STORMKEEPER_EMPOWERS, ignoreStacks: true },
  ],
  // Consumed after the cast resolved (F3), so the +50% applies to the cast that eats the stack.
  procs: [{ on: 'castResolved', abilityIds: STORMKEEPER_EMPOWERS, consumeStack: true, effects: [] }],
  ui: { glow: { abilityIds: [A.lightningBolt, A.chainLightning] }, stackCounterOn: [A.lightningBolt, A.chainLightning] },
};

/** Flame Shock's DoT. Byte-identical copy lives in the Spreading Flame talent. */
const FLAME_SHOCK_DOT: AuraDef = {
  id: AURA.flameShock,
  name: 'Flame Shock',
  kind: 'debuff',
  durationMs: 18000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 2000, hasteScaled: true, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.9 } }] },
};

// ---------------------------------------------------------------------------
// Shared effect blocks
// ---------------------------------------------------------------------------

const self = (aura: AuraDef, stacks?: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura, ...(stacks ? { stacks } : {}) });

/** Lava Surge: +1 Lava Burst charge (wasted at max charges: the cap decision) and the instant buff. */
export const GRANT_LAVA_SURGE: readonly EffectSpec[] = [{ type: 'modifyCooldown', to: 'self', abilityId: A.lavaBurst, addCharge: true }, self(LAVA_SURGE)];

const gainStaticCharge: EffectSpec = self(STATIC_CHARGE);
const spendStaticCharge: EffectSpec = { type: 'removeAura', to: 'self', auraId: AURA.staticCharge };

/** One step toward Tempest (a Maelstrom spender or a full Arc Torrent): the 3rd clears the counter and grants Tempest. */
const gatheringStormStep: EffectSpec = {
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: AURA.gatheringStorm, minStacks: 2 },
  then: [{ type: 'removeAura', to: 'self', auraId: AURA.gatheringStorm }, self(TEMPEST_READY)],
  else: [self(GATHERING_STORM)],
};

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

const lightningBolt: AbilityDef = {
  id: A.lightningBolt,
  name: 'Lightning Bolt',
  school: 'nature',
  description: 'Filler. Generates 8 Maelstrom and 1 Static Charge. Becomes Tempest while Tempest is active.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 1800 },
  gain: [{ resource: 'maelstrom', amount: 8 }],
  effects: [{ type: 'damage', school: 'nature', amount: { coef: 4.0, variance: 0.05 } }, gainStaticCharge],
  ui: { tags: ['builder'], color: '#74b9ff' },
};

/** Key 1 while Tempest is up (every 3rd Maelstrom spender). */
const tempest: AbilityDef = {
  id: A.tempest,
  name: 'Tempest',
  school: 'nature',
  description: 'Replaces Lightning Bolt after every third Earth Shock, Earthquake or full Arc Torrent. Strikes the target and up to 4 nearby enemies. Generates 10 Maelstrom and 1 Static Charge.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 1500 },
  usableIf: { type: 'selfHasAura', auraId: AURA.tempest },
  gain: [{ resource: 'maelstrom', amount: 10 }],
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 7.6, variance: 0.05 } },
    { type: 'damage', to: 'area', area: othersNearTarget(8, 4), school: 'nature', amount: { coef: 3.8, variance: 0.05 } },
    gainStaticCharge,
    { type: 'removeAura', to: 'self', auraId: AURA.tempest },
  ],
  ui: { tags: ['aoe', 'builder'], color: '#4aa3ff', animAs: A.lightningBolt },
};

const lavaBurst: AbilityDef = {
  id: A.lavaBurst,
  name: 'Lava Burst',
  school: 'fire',
  description:
    'Always crits if your Flame Shock is on the target. 2 charges. Instant with Lava Surge. Generates 10 Maelstrom and grants Master of the Elements.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 1750 },
  cooldown: { durationMs: 10000, charges: 2 },
  gain: [{ resource: 'maelstrom', amount: 10 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'targetHasAura', auraId: AURA.flameShock, fromSelf: true },
      then: [{ type: 'damage', school: 'fire', amount: { coef: 3.5, variance: 0.05 }, critBonus: 1 }],
      else: [{ type: 'damage', school: 'fire', amount: { coef: 3.5, variance: 0.05 } }],
    },
    self(MASTER_OF_ELEMENTS),
  ],
  ui: { tags: ['builder'], color: '#e17055' },
};

const chainLightning: AbilityDef = {
  id: A.chainLightning,
  name: 'Chain Lightning',
  school: 'nature',
  description: 'Hit the target and 2 nearby enemies. Each enemy hit generates 4 Maelstrom and 1 Static Charge.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'cast', timeMs: 2000 },
  // Maelstrom and Static Charge per enemy hit come from the hidden Chain Lightning passive below.
  effects: [{ type: 'damage', to: 'area', area: aroundTarget(10, 3), school: 'nature', amount: { coef: 3.0, variance: 0.05 } }],
  ui: { tags: ['builder', 'aoe'], color: '#74b9ff' },
};

const earthquake: AbilityDef = {
  id: A.earthquake,
  name: 'Earthquake',
  school: 'nature',
  description: 'Spend 60 Maelstrom: shake the ground for 6 s. Consumes all Static Charge for +8% damage per stack, locked in when cast.',
  targeting: 'ground',
  range: SPELL_RANGE,
  canCastWhileMoving: true,
  cost: [{ resource: 'maelstrom', amount: 60 }],
  effects: [
    {
      type: 'zone',
      zoneId: 'stormcaller.earthquake',
      anchor: 'point',
      shape: { type: 'circle', radius: 8 },
      durationMs: 6000,
      affects: 'enemies',
      uniquePerCaster: false,
      // F9: the ticks keep the Static Charge bonus after the stacks are removed below.
      snapshot: true,
      tick: { intervalMs: 1000, hasteScaled: true, effects: [{ type: 'damage', school: 'nature', amount: { coef: 1.2 }, isAoE: true }] },
    },
    spendStaticCharge,
    gatheringStormStep,
  ],
  ui: { tags: ['aoe', 'spender'], color: '#a29bfe' },
};

/** New (key 5): the single-target Maelstrom spender that cashes in Static Charge. */
const earthShock: AbilityDef = {
  id: A.earthShock,
  name: 'Earth Shock',
  school: 'nature',
  description: 'Spend 50 Maelstrom: instant nature damage. Consumes all Static Charge for +8% damage per stack. Empowered by Master of the Elements.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cost: [{ resource: 'maelstrom', amount: 50 }],
  effects: [{ type: 'damage', school: 'nature', amount: { coef: 5.0, variance: 0.05 } }, spendStaticCharge, gatheringStormStep],
  ui: { tags: ['spender'], color: '#b8860b' },
};

/** New (key 6): the 30 s short burst window. A full channel sets up instant Lava Burst into a 5-stack Earth Shock. */
const arcTorrent: AbilityDef = {
  id: A.arcTorrent,
  name: 'Arc Torrent',
  school: 'nature',
  description:
    'Channel lightning into the target for 2.5 s, arcing to 1 nearby enemy. Each tick generates 4 Maelstrom. If the channel completes, gain Lava Surge and 5 Static Charge, and it counts toward Tempest like a spender.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  requiresFacing: true,
  cast: { type: 'channel', durationMs: 2500, ticks: 5 },
  cooldown: { durationMs: 30000 },
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 1.4, variance: 0.05 } },
    { type: 'damage', to: 'area', area: othersNearTarget(8, 1), school: 'nature', amount: { coef: 0.7, variance: 0.05 } },
    { type: 'resource', to: 'self', resource: 'maelstrom', amount: 4 },
  ],
  // F10: only a completed channel pays off. Static Charge is SET to 5 (5 stacks on a max-5 aura).
  channelEndEffects: [...GRANT_LAVA_SURGE, self(STATIC_CHARGE, 5), gatheringStormStep],
  channelEndOn: 'complete',
  ui: { tags: ['cooldown', 'builder'], color: '#55c1ff' },
};

const stormkeeper: AbilityDef = {
  id: A.stormkeeper,
  name: 'Stormkeeper',
  school: 'nature',
  description: 'Your next 3 Lightning Bolts, Tempests or Chain Lightnings are instant and deal 50% more damage.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 60000 },
  effects: [self(STORMKEEPER, 3)],
  ui: { tags: ['cooldown'], color: '#0984e3' },
};

const flameShock: AbilityDef = {
  id: A.flameShock,
  name: 'Flame Shock',
  school: 'fire',
  description: 'Burn the target over 18 s. Lava Burst always crits on it, and its ticks can grant Lava Surge.',
  targeting: 'enemy',
  range: SPELL_RANGE,
  effects: [{ type: 'damage', school: 'fire', amount: { coef: 2.0, variance: 0.05 } }, { type: 'applyAura', aura: FLAME_SHOCK_DOT }],
  ui: { tags: [], color: '#d63031' },
};

const windShear: AbilityDef = {
  id: A.windShear,
  name: 'Wind Shear',
  school: 'nature',
  description: 'Interrupt the target and lock its school for 3 s.',
  targeting: 'enemy',
  range: 30,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 12000 },
  effects: [interrupt(3000)],
  ui: { tags: ['interrupt'], color: '#ffffff' },
};

const astralShift: AbilityDef = {
  id: A.astralShift,
  name: 'Astral Shift',
  school: 'nature',
  description: 'Reduce damage taken by 40% for 8 s.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90000 },
  effects: [selfBuff(AURA.astralShift, 'Astral Shift', 8000, [{ stat: 'damageTakenPct', value: -0.4 }])],
  ui: { tags: ['defensive'], color: '#81ecec' },
};

// ---------------------------------------------------------------------------
// Hidden passives (one ProcSpec per random source = one pity counter each)
// ---------------------------------------------------------------------------

/** Lava Surge roll: every Flame Shock hit/tick, 6% with +3% per miss (reset on a proc), at most once per 4 s (Spreading Flame / haste cap). Mean ≈ 6 ticks. */
export const LAVA_SURGE_PASSIVE: AuraDef = {
  id: PASSIVE.stormLavaSurge,
  name: 'Lava Surge',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  // Flame Shock's DoT ticks carry the aura id, which is the ability id, so one filter catches every tick.
  procs: [{ on: 'damageDealt', abilityIds: [A.flameShock], chance: 0.06, icdMs: 4000, badLuck: { step: 0.03 }, effects: [...GRANT_LAVA_SURGE] }],
};

/** Chain Lightning: +4 Maelstrom and +1 Static Charge per enemy hit. */
const CL_OVERFLOW: AuraDef = {
  id: PASSIVE.stormClOverflow,
  name: 'Chain Lightning',
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [{ on: 'damageDealt', abilityIds: [A.chainLightning], effects: [{ type: 'resource', to: 'self', resource: 'maelstrom', amount: 4 }, gainStaticCharge] }],
};

export const stormcaller: ClassDef = {
  id: CLASS.stormcaller,
  name: 'Stormcaller',
  role: 'dps',
  resources: [{ id: 'maelstrom', name: 'Maelstrom', max: 100, start: 0, regenPerSecOoc: -2 }],
  binds: {
    '1': A.lightningBolt,
    '2': A.lavaBurst,
    '3': A.chainLightning,
    '4': A.earthquake,
    Q: A.stormkeeper,
    E: A.flameShock,
    R: A.windShear,
    F: A.astralShift,
  },
  // G stays empty (reserved for a future utility).
  extraBinds: { '5': A.earthShock, '6': A.arcTorrent },
  // Tempest is the key-1 form (gated by usableIf, so a stray direct press fails).
  extraAbilities: [A.tempest, ABILITY.common.dodge],
  abilities: [lightningBolt, lavaBurst, chainLightning, earthquake, stormkeeper, flameShock, windShear, astralShift, earthShock, arcTorrent, tempest, dodge],
  passives: [LAVA_SURGE_PASSIVE, CL_OVERFLOW],
  baseStats: baseStats({ armor: 0.08 }),
  maxHp: PLAYER_HP.ranged,
  moveSpeed: 7,
  melee: null,
  color: '#2e86de',
  // +field: hero-level ability gates (unlocks.ts).
  unlocks: STORMCALLER_UNLOCKS,
};
